import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { api } from '../services/api';
import {
  useLiveChatNotifications,
  getInitials,
  getAvatarColor,
  type ChatNotificationItem,
} from '../hooks/useLiveChatNotifications';

function AvatarBubble({
  name,
  size = 'md',
  showBadge,
  onDismiss,
}: {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  showBadge?: boolean;
  onDismiss?: () => void;
}) {
  const { t } = useTranslation();
  const sizeClass = size === 'lg' ? 'w-12 h-12 text-base' : size === 'sm' ? 'w-9 h-9 text-xs' : 'w-11 h-11 text-sm';
  return (
    <div className={`relative flex-shrink-0 group ${sizeClass}`}>
      <div
        className={`${sizeClass} rounded-full ${getAvatarColor(name)} text-white font-semibold flex items-center justify-center shadow-card ring-2 ring-surface`}
      >
        {getInitials(name)}
      </div>
      {showBadge && (
        <span className="absolute -top-0.5 -start-0.5 w-3.5 h-3.5 bg-danger-500 rounded-full ring-2 ring-surface" />
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          className="absolute -top-1 -end-1 w-5 h-5 bg-inverse text-on-inverse rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-card"
          aria-label={t('chatDock.hide')}
        >
          <X size={12} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export default function ChatNotificationDock() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // FIX-NOTIF N-5: the incoming message pops up as the shared notification toast (useNotificationToasts);
  // this dock keeps only the standing avatar stack of unanswered chats.
  const { chatItems, totalUnread } = useLiveChatNotifications();

  // Best-effort side effect of opening the chat (the navigation is the user's
  // action); a failed read-mark just leaves the badge until the next refresh.
  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const openChat = useCallback((item: ChatNotificationItem) => {
    markReadMutation.mutate(item.id);
    navigate(`/tickets/${item.ticketId}?chat=1`);
  }, [markReadMutation, navigate]);

  const dismissProfile = useCallback((item: ChatNotificationItem) => {
    markReadMutation.mutate(item.id);
  }, [markReadMutation]);

  // D-326 round 2: on the chat screen in mobile the floating avatars sit over the chat - hidden there.
  const onChatScreen = useLocation().pathname.startsWith('/discussions');

  if (totalUnread === 0) return null;

  return (
    <div className={`${onChatScreen ? 'max-md:hidden ' : ''}chat-dock fixed start-4 md:start-[17rem] bottom-[calc(5.5rem+env(safe-area-inset-bottom))] md:bottom-6 z-toast flex flex-col items-end gap-2 pointer-events-none`}>
      {chatItems.length > 0 && (
        <div className="pointer-events-auto flex flex-col items-end gap-2 py-2 px-1.5 bg-surface-0/95 backdrop-blur-sm rounded-2xl shadow-float border border-surface-200">
          {chatItems.slice(0, 8).map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => openChat(item)}
              title={`${item.senderName}: ${item.messagePreview}`}
              aria-label={`${item.senderName} — ${item.ticketTitle}`}
              className="relative group"
            >
              <AvatarBubble
                name={item.senderName}
                showBadge
                onDismiss={() => dismissProfile(item)}
              />
              <span className="absolute start-full ms-2 top-1/2 -translate-y-1/2 px-2.5 py-1.5 bg-inverse text-on-inverse text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50 max-w-52 truncate">
                <span className="font-semibold">{item.senderName}</span>
                <br />
                <span className="opacity-75">{item.messagePreview}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
